import React, { useMemo } from 'react';
import { Card, Button, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FiCheck } from 'react-icons/fi';
import { saveBillBatch } from './billParsingSlice';
import { LoadingState } from '../../utilities/constants';
import { parseUserInfo } from '../../utilities/common';

const ParsedDataView = ({ parsedData, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orgId } = parseUserInfo(useSelector((state) => state.users.user));
  const { batchSaveStatus, batchSaveResult } = useSelector((state) => state.billParsing);

  const billsList = useMemo(() => {
    if (!parsedData) return null;

    const billsArray = Array.isArray(parsedData)
      ? parsedData
      : parsedData.results || parsedData.bills || parsedData.data || [];

    if (!Array.isArray(billsArray) || billsArray.length === 0) return null;

    return billsArray.filter((bill) => bill.parsedData?.metadata);
  }, [parsedData]);

  const errorsByFileName = useMemo(() => {
    if (!batchSaveResult?.summary?.errors) return {};

    const errorMap = {};
    batchSaveResult.summary.errors.forEach((err) => {
      errorMap[err.fileName] = err.error;
    });
    return errorMap;
  }, [batchSaveResult]);

  const isSaving = batchSaveStatus === LoadingState.pending;

  const handleConfirmSave = async () => {
    const billsToSave = billsList.map((bill) => ({
      tempS3Key: bill.tempS3Key,
      fileName: bill.fileName,
      parsedData: {
        metadata: bill.parsedData.metadata,
        meters: bill.parsedData.meters
      }
    }));

    const result = await dispatch(saveBillBatch({ orgId, bills: billsToSave }));

    if (result.meta.requestStatus === 'fulfilled') {
      const failedBills = result.payload?.summary?.failedBills || 0;
      if (failedBills === 0) {
        onSuccess();
      }
    }
  };

  const DataField = ({ label, value, required = false }) => (
    <div className="mb-2">
      <div className="d-flex align-items-start">
        <label className="text-muted mr-2" style={{ fontSize: '14px', minWidth: '150px' }}>
          {label}
          {required && <span className="text-danger">*</span>}:
        </label>
        <span style={{ fontSize: '14px', fontWeight: 500 }}>{value || '—'}</span>
      </div>
    </div>
  );

  if (!billsList || billsList.length === 0) {
    return <Alert variant="warning">{t('No valid bills found in the uploaded files')}</Alert>;
  }

  return (
    <Card>
      <Card.Body>
        <h5 className="mb-1">{t('Data Extraction & Mapping')}</h5>
        <p className="text-muted mb-4" style={{ fontSize: '13px' }}>
          {t('Upload a bill and map extracted data to the system')}
        </p>

        {billsList.map((bill, billIndex) => {
          const { metadata, meters } = bill.parsedData;
          const error = errorsByFileName[bill.fileName];
          return (
            <Card key={billIndex} className="mb-3">
              {billsList.length > 1 && (
                <Card.Header>
                  <strong>{bill.fileName}</strong>
                  {error && <span className="ml-2 badge badge-danger">{t('Error')}</span>}
                </Card.Header>
              )}
              <Card.Body>
                <div>
                  <DataField label={t('Entity Name')} value={metadata?.entityName} required />
                  {metadata?.billNumber && <DataField label={t('Bill/Invoice Number')} value={metadata?.billNumber} />}
                  {metadata?.provider && <DataField label={t('Provider')} value={metadata?.provider} />}
                  <DataField label={t('Currency')} value={metadata?.currency} />
                </div>

                <h6 className="mt-4 mb-3">{t('Meters')}</h6>
                {meters && meters.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>{t('Meter Number')}</th>
                          <th>{t('Utility')}</th>
                          <th>{t('Period Start Date')}</th>
                          <th>{t('Period End Date')}</th>
                          <th>{t('Contribution')}</th>
                          <th>{t('Unit')}</th>
                          <th>{t('Cost')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {meters.map((meter, meterIndex) => (
                          <tr key={meterIndex}>
                            <td>{meter.meterNumber || '—'}</td>
                            <td>{meter.utility || '—'}</td>
                            <td>{meter.periodStart || '—'}</td>
                            <td>{meter.periodEnd || '—'}</td>
                            <td>{meter.contribution || '—'}</td>
                            <td>{meter.contributionUnit || '—'}</td>
                            <td>{meter.cost || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Alert variant="warning">{t('No meters found in the bill')}</Alert>
                )}

                {error && (
                  <Alert variant="danger" className="mt-3">
                    <strong>{t('Validation Error')}:</strong> {error}
                  </Alert>
                )}
              </Card.Body>
            </Card>
          );
        })}

        <div className="d-flex justify-content-end mt-4">
          <Button variant="outline-secondary" onClick={onCancel} disabled={isSaving} className="mr-2">
            {t('Cancel')}
          </Button>
          <Button variant="dark" onClick={handleConfirmSave} disabled={billsList.length === 0 || isSaving}>
            {isSaving ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" />
                {t('Saving...')}
              </>
            ) : (
              <>
                <FiCheck className="mr-2" />
                {t('Confirm & Save')}
              </>
            )}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ParsedDataView;
